import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import './sampleApproval.css';

/**
 * The approval stamp on a sample run that has been approved for production.
 *
 * Kyu, 3 September: "make them be able to approve. with watermark." Once a
 * run is approved, the decision should be visible ON the samples — not a
 * badge in a corner that a factory scrolling a job page can miss — the way a
 * superseded PO carries SUPERSEDED across it (PoDocumentView).
 *
 * Two parts, both from the one wrapper:
 *
 *   - A line: "Approved for production by <name> · <date>". The name is read
 *     from profiles, which a TAP user may read and a supplier may not
 *     (profiles_select_tap), so a factory sees "by TAP" and never a TAP
 *     person's name — the same privacy line supplier_privacy_and_visibility
 *     draws everywhere else.
 *   - The watermark: APPROVED across every chosen photo in the run. That is
 *     CSS on the wrapper class (sampleApproval.css), so the four places a run
 *     is drawn need no prop threaded through SupplierSampleSlots or
 *     SampleReviewPanel — they render as they always did, and the stamp lands
 *     on whichever option carries verdict 'approved'.
 *
 * A run that is not approved renders its children untouched.
 */

function useApproverName(profileId: string | null, enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: ['approver-name', profileId],
    queryFn: async (): Promise<string> => {
      if (profileId === null) return '';
      const { data, error } = await client
        .from('profiles')
        .select('display_name')
        .eq('id', profileId)
        .maybeSingle();
      if (error) {
        throw new Error(error.message);
      }
      return data?.display_name ?? '';
    },
    enabled: enabled && profileId !== null,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** "Sep 3, 2026" — a decision is dated with its year; it is read months later. */
export function approvalDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function approvalLine(approver: string, decidedAt: string | null): string {
  const by = approver.trim() === '' ? 'TAP' : approver;
  return decidedAt === null
    ? `Approved for production by ${by}`
    : `Approved for production by ${by} · ${approvalDate(decidedAt)}`;
}

export function SampleApproval({
  status,
  decidedBy,
  decidedAt,
  children,
}: {
  status: string;
  decidedBy: string | null;
  /** Null where the caller does not have it (the supplier's job page). */
  decidedAt: string | null;
  children: ReactNode;
}) {
  const { access } = useAuth();
  const viewerIsTap = access.status === 'approved' && access.profile.account_type === 'tap';
  const approver = useApproverName(decidedBy, viewerIsTap && status === 'approved');

  if (status !== 'approved') {
    return <>{children}</>;
  }

  return (
    <div className="sample-approved">
      <p className="sample-approved-line" role="status">
        <span className="sample-approved-mark" aria-hidden="true">
          Approved
        </span>
        {approvalLine(viewerIsTap ? (approver.data ?? '') : '', decidedAt)}
      </p>
      {children}
    </div>
  );
}
